import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, ArrowRight } from "lucide-react";
import {
  MECHANISMS_BY_SLUG,
  MECHANISMS,
  PRODUCTS_BY_SLUG,
  IMG,
  CASE_STUDIES,
} from "@/lib/site";
import SEO from "@/components/SEO";

const Section = ({ label, title, children, id }) => (
  <div id={id} className="border-t border-zinc-200 pt-10 mt-10 first:border-t-0 first:pt-0 first:mt-0">
    <div className="tech-label text-[#C2410C]">// {label}</div>
    <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight mt-3 leading-tight">
      {title}
    </h2>
    <div className="mt-6 text-zinc-700 text-base md:text-lg leading-relaxed space-y-4">
      {children}
    </div>
  </div>
);

export default function MechanismDetail() {
  const { slug } = useParams();
  const mechanism = MECHANISMS_BY_SLUG[slug];

  if (!mechanism) return <Navigate to="/mechanisms" replace />;

  const idx = MECHANISMS.findIndex((m) => m.slug === slug);
  const prev = idx > 0 ? MECHANISMS[idx - 1] : null;
  const next = idx < MECHANISMS.length - 1 ? MECHANISMS[idx + 1] : null;

  const productObjs = (mechanism.products || [])
    .map((s) => PRODUCTS_BY_SLUG[s])
    .filter(Boolean);
  const relatedCS = (mechanism.related || [])
    .map((id) => CASE_STUDIES.find((c) => c.id === id))
    .filter(Boolean);

  return (
    <div data-testid={`mechanism-detail-${mechanism.slug}`}>
      <SEO
        title={mechanism.name}
        description={`${mechanism.tagline}. ${mechanism.headline}`}
      />
      {/* Hero */}
      <section className="relative border-b border-zinc-900 bg-zinc-950 overflow-hidden">
        {mechanism.illustration && (
          <div className="absolute inset-0">
            <img
              src={mechanism.illustration}
              alt={`${mechanism.name} technical schematic`}
              fetchPriority="high"
              className="w-full h-full object-cover opacity-45"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/70 to-zinc-950/40" />
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-20 md:pt-32 md:pb-28">
          <Link
            to="/mechanisms"
            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.24em] text-zinc-300 hover:text-[#D94515] transition-colors reveal"
            data-testid="back-to-mechanisms"
          >
            <ChevronLeft size={14} /> All Mechanisms
          </Link>
          <div className="mt-6 flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.24em] text-[#D94515] reveal reveal-delay-1">
            <span className="inline-block h-2 w-2 bg-[#D94515]" />
            <span>// {mechanism.code} · MECHANISM</span>
          </div>
          <h1 className="hero-heading text-white text-[42px] sm:text-6xl md:text-7xl lg:text-8xl mt-4 max-w-5xl reveal reveal-delay-2">
            {mechanism.name}
          </h1>
          <div className="mt-6 h-px w-24 bg-[#D94515] reveal reveal-delay-2" />
          <p className="mt-8 max-w-3xl text-lg md:text-xl text-zinc-200 leading-relaxed reveal reveal-delay-3">
            {mechanism.headline}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sticky TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <div className="tech-label">// On this page</div>
              <nav className="mt-4 space-y-2 text-sm">
                {[
                  ["what", "What it is"],
                  ["causes", "Causes"],
                  ["symptoms", "Symptoms"],
                  ["diagnosis", "How we diagnose"],
                  ["repair", "How we repair"],
                  ...(productObjs.length ? [["products", "Products used"]] : []),
                  ...(relatedCS.length ? [["related", "Related case studies"]] : []),
                ].map(([id, label]) => (
                  <a
                    key={id}
                    href={`#${id}`}
                    className="block border-l-2 border-zinc-200 hover:border-[#C2410C] hover:text-[#C2410C] pl-3 py-1 text-zinc-700 transition-colors"
                    data-testid={`toc-${id}`}
                  >
                    {label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-9">
            <Section id="what" label="What it is" title={mechanism.tagline}>
              <p>{mechanism.what}</p>
            </Section>

            <Section id="causes" label="Causes" title="Where it comes from">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {mechanism.causes.map((c, i) => (
                  <li key={i} className="flex items-start gap-3 border border-zinc-200 p-4">
                    <span className="font-mono text-[11px] text-[#C2410C] tracking-[0.16em] shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm text-zinc-800">{c}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section id="symptoms" label="Symptoms" title="What you might see on site">
              <ul className="space-y-3">
                {mechanism.symptoms.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 border-b border-zinc-200 pb-3">
                    <Check size={18} className="mt-1 text-[#C2410C] shrink-0" strokeWidth={2.5} />
                    <span className="text-zinc-800">{s}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section id="diagnosis" label="Diagnosis" title="How we investigate">
              <ul className="space-y-3">
                {mechanism.diagnosis.map((d, i) => (
                  <li key={i} className="flex items-start gap-3 border-b border-zinc-200 pb-3">
                    <span className="font-mono text-[11px] text-zinc-500 tracking-[0.16em] shrink-0 mt-1">
                      T · {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-zinc-800">{d}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section id="repair" label="Repair" title="How we put it right">
              <p>{mechanism.repair}</p>
            </Section>

            {productObjs.length > 0 && (
              <Section id="products" label="Products used" title="Recommended Fosroc products">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
                  {productObjs.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/products#${p.slug}`}
                      className="bg-white p-5 group hover:bg-zinc-50 transition-colors flex gap-4"
                      data-testid={`mechanism-product-${p.slug}`}
                    >
                      {p.image && (
                        <div className="w-24 shrink-0 border border-zinc-200 bg-zinc-50 p-1.5 self-start">
                          <div className="aspect-[300/186] overflow-hidden">
                            <img
                              src={p.image}
                              alt=""
                              loading="lazy"
                              className="w-full h-full object-contain"
                            />
                          </div>
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                          {p.brand} · {p.category}
                        </div>
                        <div className="font-display text-xl md:text-2xl font-bold uppercase mt-2 group-hover:text-[#C2410C] transition-colors leading-tight">
                          {p.name}
                        </div>
                        <p className="mt-2 text-sm text-zinc-600">{p.tagline}</p>
                        <div className="mt-3 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-900 flex items-center gap-1.5">
                          View spec <ArrowRight size={12} />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {relatedCS.length > 0 && (
              <Section id="related" label="Related" title="Case studies of this mechanism">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
                  {relatedCS.map((cs) => (
                    <Link
                      key={cs.id}
                      to="/case-studies"
                      className="bg-white group"
                      data-testid={`mechanism-related-${cs.id}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={IMG[cs.image]}
                          alt={cs.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 to-transparent" />
                        <div className="absolute bottom-3 left-3 right-3 text-white">
                          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D94515]">
                            REF · {cs.ref}
                          </div>
                          <div className="font-display text-lg font-bold uppercase mt-1 leading-tight">
                            {cs.title}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </Section>
            )}
          </article>
        </div>
      </section>

      {/* Bottom CTA + prev/next */}
      <section className="border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {prev ? (
            <Link
              to={`/mechanisms/${prev.slug}`}
              className="p-8 border border-zinc-900 bg-white hover:bg-zinc-900 hover:text-white transition-colors group"
              data-testid="mechanism-prev"
            >
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 group-hover:text-zinc-300">
                &larr; Previous mechanism
              </div>
              <div className="font-display text-2xl md:text-3xl font-bold uppercase mt-2 leading-tight">
                {prev.name}
              </div>
            </Link>
          ) : (
            <div />
          )}
          {next ? (
            <Link
              to={`/mechanisms/${next.slug}`}
              className="p-8 border border-zinc-900 bg-white hover:bg-zinc-900 hover:text-white transition-colors group text-right"
              data-testid="mechanism-next"
            >
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 group-hover:text-zinc-300">
                Next mechanism &rarr;
              </div>
              <div className="font-display text-2xl md:text-3xl font-bold uppercase mt-2 leading-tight">
                {next.name}
              </div>
            </Link>
          ) : (
            <div />
          )}
        </div>

        <div className="max-w-7xl mx-auto px-6 pb-20">
          <div className="relative border border-zinc-900 p-8 md:p-14 bg-white">
            <span className="corner-tick tl" />
            <span className="corner-tick tr" />
            <span className="corner-tick bl" />
            <span className="corner-tick br" />
            <div className="tech-label">// Talk to us</div>
            <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight mt-3 leading-[0.95] max-w-3xl">
              Seeing signs of {mechanism.name.toLowerCase()} on your structure?
            </h3>
            <div className="mt-6">
              <Link to="/contact" className="btn-primary" data-testid={`mechanism-cta-${mechanism.slug}`}>
                Request a diagnosis <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
